import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Label,
  LabelList,
  Tooltip,
  Cell,
  ResponsiveContainer,
  TooltipProps,
} from 'recharts';
import { formatNumberToKMB } from 'src/utils/datasets';

interface DataPoint {
  label: string | number;
  value: number;
}

interface ChartProps {
  title: string;
  xAxisName: string;
  yAxisName: string;
  data: DataPoint[];
  barsTouching?: boolean;
  isCategorical?: boolean;
  startColor?: string;
  endColor?: string;
  rotateXAxisTicks?: boolean;
  showXAxisName?: boolean;
  sortBars?: boolean;
  binSize?: number;
  formatBinLabel?: (start: number, end: number) => string;
  bottomMargin?: number;
  useKMBFormat?: boolean;
  showBarLabels?: boolean;
  chartPadding?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
  tickFontSize?: number;
  totalCount?: number;
}

interface CustomTooltipProps extends TooltipProps<number, string> {
  active?: boolean;
  payload?: Array<{
    value: number;
    payload: DataPoint;
  }>;
}

// Helper function to interpolate between two colors
const interpolateColor = (color1: string, color2: string, factor: number): string => {
  const c1 = {
    r: parseInt(color1.slice(1, 3), 16),
    g: parseInt(color1.slice(3, 5), 16),
    b: parseInt(color1.slice(5, 7), 16),
  };

  const c2 = {
    r: parseInt(color2.slice(1, 3), 16),
    g: parseInt(color2.slice(3, 5), 16),
    b: parseInt(color2.slice(5, 7), 16),
  };

  const r = Math.round(c1.r + (c2.r - c1.r) * factor);
  const g = Math.round(c1.g + (c2.g - c1.g) * factor);
  const b = Math.round(c1.b + (c2.b - c1.b) * factor);

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

const DistributionChart: React.FC<ChartProps> = ({
  title,
  xAxisName,
  yAxisName,
  data,
  barsTouching = true,
  isCategorical = false,
  startColor = '#1E40AF',
  endColor = '#3B82F6',
  rotateXAxisTicks = false,
  showXAxisName = true,
  binSize = 0,
  formatBinLabel = (start, end) => `${start}-${end}`,
  bottomMargin = rotateXAxisTicks ? 150 : 20,
  useKMBFormat = false,
  showBarLabels = true,
  chartPadding = {
    top: 50,
    right: 20,
    bottom: 20,
    left: 20,
  },
  tickFontSize = 12,
  totalCount,
}) => {
  // Function to format numbers based on the useKMBFormat prop
  const formatValue = (value: number): string => {
    return useKMBFormat ? formatNumberToKMB(value) : value.toLocaleString();
  };

  // Function to bin numerical data
  const { finalData, total } = React.useMemo(() => {
    // 1) Split numeric vs extras
    const numericPoints: DataPoint[] = [];
    const extras: DataPoint[] = [];
    data.forEach((d) => {
      const n = typeof d.label === 'number' ? d.label : parseFloat(d.label as string);
      Number.isFinite(n) ? numericPoints.push({ label: n, value: d.value }) : extras.push(d);
    });

    let arranged: DataPoint[];

    // 2) Bin if requested
    if (!isCategorical && binSize > 0) {
      const labels = numericPoints.map((d) => d.label as number);
      const minLabel = Math.min(...labels);
      const maxLabel = Math.max(...labels);
      const binCount = Math.ceil((maxLabel - minLabel) / binSize);
      const bins: Record<string, number> = {};

      // init bins
      for (let i = 0; i < binCount; i++) {
        const start = minLabel + i * binSize;
        const end = start + binSize;
        bins[formatBinLabel(start, end)] = 0;
      }

      // fill bins
      numericPoints.forEach(({ label, value }) => {
        const idx = Math.floor(((label as number) - minLabel) / binSize);
        const start = minLabel + idx * binSize;
        const end = start + binSize;
        const key = formatBinLabel(start, end);
        bins[key] = (bins[key] || 0) + value;
      });

      const binned = Object.entries(bins).map(([label, value]) => ({ label, value }));
      arranged = extras.length ? [...binned, ...extras] : binned;
    } else {
      // 3) No binning: just sort numericPoints ascending + extras
      numericPoints.sort((a, b) => (a.label as number) - (b.label as number));
      arranged = extras.length ? [...numericPoints, ...extras] : numericPoints;
    }

    // 4) Compute total
    const sum = arranged.reduce((acc, pt) => acc + pt.value, 0);
    return { finalData: arranged, total: totalCount ?? sum };
  }, [data, isCategorical, binSize, formatBinLabel, totalCount]);

  const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;

    const percentage = ((payload[0].value / total) * 100).toFixed(1);

    return (
      <div className="bg-neutral-900 border border-gray-700 shadow-xl p-4 rounded">
        <p className="text-gray-300">
          {xAxisName}: {payload[0].payload.label}
        </p>
        <p className="font-semibold text-gray-300">
          {yAxisName}: {formatValue(payload[0].value)} ({percentage}%)
        </p>
      </div>
    );
  };

  const renderCustomLabel = (props: any) => {
    const { x, y, width, value } = props;
    const percentage = (value / total) * 100;
    const formattedPercentage =
      percentage < 1 ? percentage.toFixed(1) : Math.round(percentage).toString();

    return (
      <text
        x={x + width / 2}
        y={y - 10}
        fill="#FFFFFF"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={tickFontSize}
      >
        {typeof value === 'number' ? `${formattedPercentage}%` : value}
      </text>
    );
  };

  // Generate colors for each bar
  const getBarColor = (index: number) => {
    const factor = finalData.length === 1 ? 0 : index / (finalData.length - 1);
    return interpolateColor(startColor, endColor, factor);
  };

  return (
    <div className="w-full h-[500px] bg-background-dark-subdued p-4 rounded-lg">
      <h2 className="font-semibold text-gray-100">{title}</h2>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={finalData}
          margin={{
            top: chartPadding.top,
            right: chartPadding.right,
            left: chartPadding.left,
            bottom: bottomMargin || chartPadding.bottom,
          }}
          barCategoryGap={barsTouching ? 0 : '15%'}
          barGap={0}
          layout={rotateXAxisTicks ? 'horizontal' : undefined}
        >
          <CartesianGrid
            vertical={true}
            horizontal={true}
            stroke="#374151"
            strokeDasharray="3 3"
            opacity={0.4}
          />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={{ stroke: '#4B5563' }}
            tick={(props) => {
              const { x, y, payload } = props;

              // Truncate text to 20 characters if needed
              const truncateText = (text: string | number): string => {
                const str = String(text);
                return str.length > 20 ? str.substring(0, 17) + '...' : str;
              };

              if (rotateXAxisTicks) {
                return (
                  <text
                    x={x}
                    y={y}
                    fill="#9CA3AF"
                    textAnchor="end"
                    transform={`rotate(-45, ${x}, ${y})`}
                    dy={25}
                    dx={-15}
                    fontSize={tickFontSize}
                  >
                    {truncateText(payload.value)}
                  </text>
                );
              }

              // Split truncated text into words
              const truncatedText = truncateText(payload.value);
              const words = truncatedText.split(' ');
              const lineHeight = tickFontSize + 1;

              return (
                <g transform={`translate(${x},${y})`}>
                  {words.map((word: string, index: number) => (
                    <text
                      key={index}
                      x={0}
                      y={index * lineHeight}
                      textAnchor="middle"
                      fill="#9CA3AF"
                      dy={10}
                      fontSize={tickFontSize}
                    >
                      {word}
                    </text>
                  ))}
                </g>
              );
            }}
            stroke="#4B5563"
            type="category"
            padding={{ left: 0, right: 0 }}
            interval={0}
            height={rotateXAxisTicks ? 120 : 65}
            offset={rotateXAxisTicks ? 20 : 0}
          >
            {showXAxisName && (
              <Label
                fontSize={14}
                value={xAxisName}
                position="insideBottom"
                style={{
                  fill: '#9CA3AF',
                  textAnchor: 'middle',
                  marginTop: rotateXAxisTicks ? 50 : 25,
                }}
              />
            )}
          </XAxis>
          <YAxis
            tick={{
              fill: '#9CA3AF',
              fontSize: tickFontSize,
            }}
            stroke="#4B5563"
            width={80}
            tickFormatter={formatValue}
          >
            <Label
              fontSize={14}
              value={yAxisName}
              angle={-90}
              position="insideLeft"
              style={{
                fill: '#9CA3AF',
                textAnchor: 'middle',
              }}
            />
          </YAxis>
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
          <Bar dataKey="value">
            {finalData.map((_, index) => (
              <Cell
                key={`cell-${index}`}
                fill={getBarColor(index)}
                stroke={getBarColor(index)}
                strokeWidth={2}
              />
            ))}
            {showBarLabels && <LabelList dataKey="value" content={renderCustomLabel} />}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default DistributionChart;
